import { useState } from 'react';
import { Modal, Button, Checkbox, TextArea, Form } from 'semantic-ui-react';

/**
 * ProviderPayOverrideModal
 *
 * Shown when a user clicks the override icon on a practice row.
 * Allows setting force_bcare_pay = true/false with an optional reason.
 *
 * Props:
 *   open           - boolean
 *   onClose        - () => void
 *   abbr           - string  (provider abbreviation)
 *   practiceDbName - string  (practice_db_name)
 *   payMonth       - string  (YYYY-MM)
 *   practiceName   - string  (display name)
 *   existingOverride - object | null  (current DB record if any)
 *   onSave         - ({ abbr, practice_db_name, pay_month, force_bcare_pay, reason }) => void
 *   onDelete       - (id) => void
 */
const ProviderPayOverrideModal = ({
    open,
    onClose,
    abbr,
    practiceDbName,
    payMonth,
    practiceName,
    existingOverride,
    deleteMode,
    onSave,
    onDelete,
    onDeleteByKey,
}) => {
    const [forceBcare, setForceBcare] = useState(
        existingOverride ? !!existingOverride.force_bcare_pay : false
    );
    const [reason, setReason] = useState(existingOverride?.reason || '');
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        setSaving(true);
        await onSave({
            abbr,
            practice_db_name: practiceDbName,
            pay_month: payMonth,
            force_bcare_pay: forceBcare,
            reason
        });
        setSaving(false);
        onClose();
    };

    const handleRemove = async () => {
        if (!existingOverride) return;
        setSaving(true);
        await onDelete(existingOverride.id, payMonth);
        setSaving(false);
        onClose();
    };

    const handleRemoveByKey = async () => {
        setSaving(true);
        await onDeleteByKey(abbr, practiceDbName, payMonth);
        setSaving(false);
        onClose();
    };

    if (deleteMode) {
        return (
            <Modal open={open} onClose={onClose} size="mini">
                <Modal.Header style={{ color: 'orange' }}>
                    Remove Override
                </Modal.Header>
                <Modal.Content>
                    <p>
                        Remove the bCare pay override for <strong>{abbr}</strong> at <strong>{practiceName}</strong>?
                    </p>
                    <p style={{ color: '#888', fontSize: '0.8rem' }}>Month: {payMonth}</p>
                </Modal.Content>
                <Modal.Actions>
                    <Button content="Cancel" onClick={onClose} disabled={saving} />
                    <Button
                        negative
                        content="Remove Override"
                        loading={saving}
                        onClick={handleRemoveByKey}
                    />
                </Modal.Actions>
            </Modal>
        );
    }

    return (
        <Modal open={open} onClose={onClose} size="small">
            <Modal.Header>
                Pay Override — {abbr} @ {practiceName}
                <div style={{ fontSize: '0.8rem', fontWeight: 'normal', color: '#666', marginTop: 4 }}>
                    Month: {payMonth}
                </div>
            </Modal.Header>
            <Modal.Content>
                <Form>
                    <Form.Field>
                        <Checkbox
                            label="Force bCare Pay (skip Greater Of comparison)"
                            checked={forceBcare}
                            onChange={(e, { checked }) => setForceBcare(checked)}
                        />
                        <div style={{ marginTop: 6, fontSize: '0.85rem', color: '#555' }}>
                            When enabled, pay is calculated as <strong>bCare × payOverBase%</strong> regardless
                            of whether Daily Rate pay would be higher.
                        </div>
                    </Form.Field>
                    <Form.Field style={{ marginTop: 16 }}>
                        <label>Reason (optional)</label>
                        <TextArea
                            placeholder="Enter reason for override..."
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={3}
                        />
                    </Form.Field>
                </Form>
            </Modal.Content>
            <Modal.Actions>
                {existingOverride && (
                    <Button
                        negative
                        floated="left"
                        content="Remove Override"
                        loading={saving}
                        onClick={handleRemove}
                    />
                )}
                <Button content="Cancel" onClick={onClose} />
                <Button
                    primary
                    content="Save"
                    loading={saving}
                    onClick={handleSave}
                />
            </Modal.Actions>
        </Modal>
    );
};

export default ProviderPayOverrideModal;
